import {
	getAlignmentDataFromControlPointsAndSegmentTypes,
	HorizontalAlignmentType,
	RoadSavedData,
	SegmentType,
	type IControlPointRetrivedData,
	type ISavedSlopeBreak,
	type ISlopeBreak,
} from "earthbrain-viewer";
import {
	ObjectType,
	type ICrossSection,
	type ICrossSectionSettings,
	type IObjectData,
} from "./types";
import store from "@/store";
import { Cartesian3 } from "cesium";
import type { IRoadObjectData } from "./RoadObject";
import { defaultLineWorksCrossSection } from "@/components/Project/LineWorks/default";

export interface IRoadObjectProps_Outdated {
	id: string;
	cid: string;
	type: number;
	name: string;
	isShow: boolean;
	isOpen: boolean;
	edit: boolean;
	panel: {
		road: boolean;
		cut: boolean;
		fill: boolean;
		volume: boolean;
	};
	cutVolume: number;
	fillVolume: number;
	transparency: number;
	info: {
		length: number;
		height: {
			min: number;
			max: number;
		};
		slope: {
			min: number;
			max: number;
		};
	};
	settings: {
		commonSettings: {
			leftWidth: number;
			rightWidth: number;
			leftSlope: number;
			rightSlope: number;
			isSuperElevation: boolean;
			interpolationPitch: number;
			surfaceColor: string;
			segmentSettings: [];
		};
		cutSettings: {
			roadHeight: number;
			moundSlope: number;
			shelfWidth: number;
			shelfSlope: number;
			bLeftRightSettings: boolean;
			generateShelf: boolean;
			leftSlopeHeight: number;
			leftMoundSlope: number;
			rightSlopeHeight: number;
			rightMoundSlope: number;
			leftShelfWidth: number;
			leftShelfSlope: number;
			rightShelfWidth: number;
			rightShelfSlope: number;
			color: string;
			outline: boolean;
		};
		embarkmentSettings: {
			roadHeight: number;
			moundSlope: number;
			shelfWidth: number;
			shelfSlope: number;
			bLeftRightSettings: boolean;
			generateShelf: boolean;
			leftSlopeHeight: number;
			leftMoundSlope: number;
			rightSlopeHeight: number;
			rightMoundSlope: number;
			leftShelfWidth: number;
			leftShelfSlope: number;
			rightShelfWidth: number;
			rightShelfSlope: number;
			color: string;
			outline: boolean;
		};
	};
	cash: {
		leftShelfWidth: number;
		rightShelfWidth: number;
	};
	isLoaded: boolean;
	isLoadedWithoutPointCloud: boolean;
	json: string;
}

export interface IRoadObjectData_Outdated extends IObjectData {
	panel: {
		road: boolean;
		cut: boolean;
		fill: boolean;
		volume: boolean;
	};
	cutVolume: number;
	fillVolume: number;
	transparency: number;
	length: number;
	heightRange: {
		min: number;
		max: number;
	};
	slopeRange: {
		min: number;
		max: number;
	};
	pathSettings: {
		leftWidth: number;
		rightWidth: number;
		leftSlope: number;
		rightSlope: number;
		isSuperElevation: boolean;
		interpolationPitch: number;
		surfaceColor: string;
	};
	cutSettings: {
		roadHeight: number;
		moundSlope: number;
		shelfWidth: number;
		shelfSlope: number;
		isLeftRightDifferent: boolean;
		generateShelf: boolean;
		leftSlopeHeight: number;
		leftMoundSlope: number;
		rightSlopeHeight: number;
		rightMoundSlope: number;
		leftShelfWidth: number;
		leftShelfSlope: number;
		rightShelfWidth: number;
		rightShelfSlope: number;
		color: string;
		outline: boolean;
	};
	fillSettings: {
		roadHeight: number;
		moundSlope: number;
		shelfWidth: number;
		shelfSlope: number;
		isLeftRightDifferent: boolean;
		generateShelf: boolean;
		leftSlopeHeight: number;
		leftMoundSlope: number;
		rightSlopeHeight: number;
		rightMoundSlope: number;
		leftShelfWidth: number;
		leftShelfSlope: number;
		rightShelfWidth: number;
		rightShelfSlope: number;
		color: string;
		outline: boolean;
	};

	controlPoints: IControlPointRetrivedData[];
	slopeBreaks: ISavedSlopeBreak[];
	segmentTypes: SegmentType[];
	associatedTerrain: { id: number; terrainType: string };
}

export interface IRoadObjectData_Version1
	extends Omit<IRoadObjectData_Outdated, "slopeBreaks"> {
	version: 1.0;
	cid: string;
	md5: string;
	slopeBreaks: ISlopeBreak[];
}

export interface IRoadObjectData_Version2 extends IRoadObjectData_Outdated {
	version: 2.0;
	cid: string;
	md5: string;
}

export interface IRoadObjectData_Version3 extends IRoadObjectData {
	version: 3.0;
	cid: string;
	md5: string;
}

type TRoadSavedData =
	| IRoadObjectData_Version3
	| IRoadObjectData_Version2
	| IRoadObjectData_Version1
	| IRoadObjectProps_Outdated;

export class RoadObjectAdapter {
	private static async fromProps_Outdated(
		data: IRoadObjectProps_Outdated,
		json: string,
	): Promise<IRoadObjectData> {
		const activePointCloudId = store.getters.activePointCloud?.getAssetIdNum();
		const viewerRoadData = await RoadSavedData.parseRoadSavedData(
			json,
			activePointCloudId,
		);
		return {
			id: data.cid,
			name: data.name,
			type: ObjectType.ROAD,
			md5: "",
			panel: data.panel,
			cutVolume: data.cutVolume,
			fillVolume: data.fillVolume,
			transparency: data.transparency,
			length: data.info.length,
			heightRange: data.info.height,
			slopeRange: data.info.slope,
			pathSettings: {
				leftWidth: data.settings.commonSettings.leftWidth,
				rightWidth: data.settings.commonSettings.rightWidth,
				leftSlope: data.settings.commonSettings.leftSlope,
				rightSlope: data.settings.commonSettings.rightSlope,
				isSuperElevation: data.settings.commonSettings.isSuperElevation,
				interpolationPitch: data.settings.commonSettings.interpolationPitch,
				surfaceColor: data.settings.commonSettings.surfaceColor,
			},
			cutSettings: {
				roadHeight: data.settings.cutSettings.roadHeight,
				moundSlope: data.settings.cutSettings.moundSlope,
				shelfWidth: data.settings.cutSettings.shelfWidth,
				shelfSlope: data.settings.cutSettings.shelfSlope,
				isLeftRightDifferent: data.settings.cutSettings.bLeftRightSettings,
				generateShelf: data.settings.cutSettings.generateShelf,
				leftSlopeHeight: data.settings.cutSettings.leftSlopeHeight,
				leftMoundSlope: data.settings.cutSettings.leftMoundSlope,
				rightSlopeHeight: data.settings.cutSettings.rightSlopeHeight,
				rightMoundSlope: data.settings.cutSettings.rightMoundSlope,
				leftShelfWidth: data.settings.cutSettings.leftShelfWidth,
				leftShelfSlope: data.settings.cutSettings.leftShelfSlope,
				rightShelfWidth: data.settings.cutSettings.rightShelfWidth,
				rightShelfSlope: data.settings.cutSettings.rightShelfSlope,
				color: data.settings.cutSettings.color,
				outline: data.settings.cutSettings.outline,
			},
			fillSettings: {
				roadHeight: data.settings.embarkmentSettings.roadHeight,
				moundSlope: data.settings.embarkmentSettings.moundSlope,
				shelfWidth: data.settings.embarkmentSettings.shelfWidth,
				shelfSlope: data.settings.embarkmentSettings.shelfSlope,
				isLeftRightDifferent:
					data.settings.embarkmentSettings.bLeftRightSettings,
				generateShelf: data.settings.embarkmentSettings.generateShelf,
				leftSlopeHeight: data.settings.embarkmentSettings.leftSlopeHeight,
				leftMoundSlope: data.settings.embarkmentSettings.leftMoundSlope,
				rightSlopeHeight: data.settings.embarkmentSettings.rightSlopeHeight,
				rightMoundSlope: data.settings.embarkmentSettings.rightMoundSlope,
				leftShelfWidth: data.settings.embarkmentSettings.leftShelfWidth,
				leftShelfSlope: data.settings.embarkmentSettings.leftShelfSlope,
				rightShelfWidth: data.settings.embarkmentSettings.rightShelfWidth,
				rightShelfSlope: data.settings.embarkmentSettings.rightShelfSlope,
				color: data.settings.embarkmentSettings.color,
				outline: data.settings.embarkmentSettings.outline,
			},
			associatedTerrain: {
				id: viewerRoadData.associatedTerrain.id,
				terrainType: viewerRoadData.associatedTerrain.terrainType,
			},
			surveyPoints: viewerRoadData.surveyPoints,
			ipPointIds: viewerRoadData.ipPointIds,
			horizontalAlignment: viewerRoadData.horizontalAlignment,
			verticalAlignment: viewerRoadData.verticalAlignment,
		};
	}

	private static async fromRoadData_Version1(
		data: IRoadObjectData_Version1,
	): Promise<IRoadObjectData> {
		const {
			surveyPoints,
			ipPointIds,
			horizontalAlignmentData,
			verticalAlignmentData,
		} = await getAlignmentDataFromControlPointsAndSegmentTypes(data);
		return {
			...data,
			surveyPoints: surveyPoints,
			ipPointIds: ipPointIds,
			horizontalAlignment: horizontalAlignmentData,
			verticalAlignment: verticalAlignmentData,
		};
	}

	private static async fromRoadData_Version2(
		data: IRoadObjectData_Version2,
	): Promise<IRoadObjectData> {
		const {
			surveyPoints,
			ipPointIds,
			horizontalAlignmentData,
			verticalAlignmentData,
		} = await getAlignmentDataFromControlPointsAndSegmentTypes(data);
		return {
			...data,
			surveyPoints: surveyPoints,
			ipPointIds: ipPointIds,
			horizontalAlignment: horizontalAlignmentData,
			verticalAlignment: verticalAlignmentData,
		};
	}

	private static fromRoadData_Version3(
		data: IRoadObjectData_Version3,
	): IRoadObjectData {
		data.horizontalAlignment.type =
			data.horizontalAlignment.type || HorizontalAlignmentType.SIMPLE_CIRCULAR;
		let crossSectionSettings: ICrossSectionSettings;
		const savedSettings: ICrossSection = (data as any).crossSection;
		if (savedSettings) {
			const defaultSettings = defaultLineWorksCrossSection();
			crossSectionSettings = {
				id: savedSettings.cross_section_id,
				leftWidth: savedSettings.left_side_width ?? defaultSettings.leftWidth,
				rightWidth:
					savedSettings.right_side_width ?? defaultSettings.rightWidth,
				interpolationPitch:
					savedSettings.station_interval ?? defaultSettings.interpolationPitch,
				samplingSize:
					savedSettings.sampling_size ?? defaultSettings.samplingSize,
			};
		}
		return {
			...data,
			surveyPoints: data.surveyPoints.map((sp) => ({
				...sp,
				wgs84Point: new Cartesian3(
					sp.wgs84Point.x,
					sp.wgs84Point.y,
					sp.wgs84Point.z,
				),
			})),
			crossSectionSetting: crossSectionSettings,
		};
	}
	public static async toRoadData(
		data: TRoadSavedData,
		json: string,
	): Promise<IRoadObjectData> {
		if ("version" in data && data.version === 3.0) {
			return this.fromRoadData_Version3(data as IRoadObjectData_Version3);
		} else if ("version" in data && data.version === 2.0) {
			return this.fromRoadData_Version2(data as IRoadObjectData_Version2);
		} else if ("version" in data && data.version === 1.0) {
			return this.fromRoadData_Version1(data as IRoadObjectData_Version1);
		} else {
			if (!json) {
				throw new Error(
					"RoadObjectAdapter.toRoadData: json is required for legacy data",
				);
			}
			return this.fromProps_Outdated(data as IRoadObjectProps_Outdated, json);
		}
	}
}
